import {onSessionCleared} from './sessionCleanup';
import {debugWarn} from './debug';

/**
 * Transcriptions the user asked us to hold until their session budget resets.
 *
 * This is the queue behind the 429 `session_limit_exceeded` offer, and it is **not** the batch
 * queue in `queueSlice` — that one is the retired desktop app's local Rust runner and is still a
 * stub. This holds jobs the server has already refused once, with the instant it said to try again.
 *
 * **It is persisted, and that is the whole point.** A session wait is up to four hours; expecting
 * the tab to stay open that long is not realistic, and a queue that evaporated on reload would
 * silently drop work the user was told we were holding. On a cold load the entries come back, and
 * anything whose release has already passed starts on the next tick.
 *
 * It is session-scoped like every other localStorage key here — one account's queued jobs must not
 * be replayed under the next account's token, which would be answered with a 403 or, worse,
 * accepted against the wrong audio id.
 */

const PENDING_KEY = 'censorme.pendingTranscriptions';

/**
 * Jobs settle in "usually 30 seconds to a few minutes" (see the row's own copy), so a job queued
 * for `too_many_in_flight` gets a short, fixed nudge rather than an exact release instant — the 429
 * carries no `resetIn`/`retryAfterSeconds` to compute one from. `pendingTranscriptions.container`
 * reuses this same value when a release attempt hits the same reason again mid-wait.
 */
export const IN_FLIGHT_RETRY_SECONDS = 120;

export interface PendingTranscription {
  /** Local id — there is no server-side row for a job that was never accepted. */
  id: string;
  audioId: string;
  /** Kept so the queue can name the file without joining against a list that may not be loaded. */
  audioName: string;
  outputFileName: string;
  projectId?: string;
  languageCode?: string;
  /** Seconds, for the "this will use ~X" line. 0 when the audio's duration was never probed. */
  durationSeconds: number;
  /**
   * ISO 8601 — the earliest this may be sent again. The session's `expiresAt` when the rejection
   * carried one, otherwise `retryAfterSeconds` from now.
   */
  releaseAt: string;
  queuedAt: string;
  /**
   * Why this is waiting — drives the copy shown for it (the release toast, the cancel tooltip).
   * `'session_limit'`: the rate throttle, a wait that can run hours. `'in_flight'`: the per-user cap
   * of 3 concurrent jobs, which clears in minutes as soon as one of the three finishes. Optional and
   * read as `'session_limit'` when absent, so entries persisted before this field existed — the only
   * kind that ever were — still show the right copy.
   */
  reason?: 'session_limit' | 'in_flight';
}

export function loadPendingTranscriptions(): PendingTranscription[] {
  try {
    const raw = localStorage.getItem(PENDING_KEY);

    if (!raw) {
      return [];
    }

    const parsed: unknown = JSON.parse(raw);

    // Anything malformed is dropped rather than repaired: this is a convenience queue, and one bad
    // entry must not stop the rest of it from being restored.
    return Array.isArray(parsed)
      ? parsed.filter((entry): entry is PendingTranscription =>
        typeof entry === 'object'
          && entry !== null
          && typeof (entry as PendingTranscription).id === 'string'
          && typeof (entry as PendingTranscription).audioId === 'string'
          && typeof (entry as PendingTranscription).releaseAt === 'string')
      : [];
  } catch (error) {
    debugWarn('[pendingTranscriptions] Could not read the queue:', error);
    return [];
  }
}

export function savePendingTranscriptions(pending: PendingTranscription[]): void {
  try {
    if (pending.length === 0) {
      localStorage.removeItem(PENDING_KEY);
      return;
    }
    localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch (error) {
    debugWarn('[pendingTranscriptions] Could not persist the queue:', error);
  }
}

export function clearPendingTranscriptions(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch (error) {
    debugWarn('[pendingTranscriptions] Could not clear the queue:', error);
  }
}

onSessionCleared(clearPendingTranscriptions);

/**
 * When a refused job may be retried.
 *
 * `sessionExpiresAt` is the better answer and is what the countdown is drawn from, but the contract
 * types it nullable, so `retryAfterSeconds` is the fallback rather than an assumption that the
 * field is always there.
 */
export function releaseInstant(
  sessionExpiresAt: string | null | undefined,
  retryAfterSeconds: number | undefined
): string {
  if (sessionExpiresAt && Number.isFinite(new Date(sessionExpiresAt).getTime())) {
    return sessionExpiresAt;
  }

  return new Date(Date.now() + (retryAfterSeconds ?? 0) * 1000).toISOString();
}
